import type { ReceiverInput } from "@pdfking/functionpool"
import { PDFMerge } from "@pdfking/renderer-default"

import { CastPDFReaderOptions } from "../../files/CastPDFReaderOptions"
import { GenerateFile } from "../../files/GenerateFile"
import type { PdfEngine } from "../../types/engine/PdfEngine"
import type { SinglePagePdf } from "../../types/files/cached/SinglePagePdf"
import { ContentType } from "../../types/files/ContentType"
import { VirtualFileType } from "../../types/files/VirtualFileType"
import type { TaskPage } from "../../types/tasks/input/TaskPage"
import type { TaskResult } from "../../types/tasks/input/TaskResult"
import type { TaskMergeOptions } from "../../types/tasks/options/TaskMergeOptions"
import { TaskName } from "../../types/tasks/TaskName"
import { TrackedTaskName } from "../../types/tasks/tracking/TrackedTaskName"
import type { TrackingCallback } from "../../types/tasks/tracking/TrackingCallback"

export const TaskMerge = async function* (
  engine: PdfEngine,
  task: TaskMergeOptions,
  emit: TrackingCallback,
  messages: ReceiverInput<TaskPage>
): AsyncGenerator<TaskResult> {
  const pages: SinglePagePdf[] = []
  let done = 0

  const emitter = (done: number, total: number) =>
    emit({
      name: TrackedTaskName.MERGE_PDF,
      done,
      total,
    })

  void emitter(0, 1)

  async function* pagesGenerator() {
    for await (const { page_number, page_count, content } of messages) {
      void emitter(done, page_count)

      const readerOptions = await CastPDFReaderOptions(
        engine,
        task.options,
        content,
        emit
      )

      pages.push(content as SinglePagePdf)

      yield {
        page_number,
        ...readerOptions,
      }

      done += 1
      void emitter(done, page_count)
    }
  }

  const content = await GenerateFile(
    engine,
    PDFMerge(pagesGenerator()),
    ContentType.PDF,
    emit
  )

  // Sort collected pages by page_number
  pages.sort((a, b) => a.page_number - b.page_number)

  yield {
    enum: TaskName.CONTENT_FILE,
    name: "merge.pdf",
    content: {
      enum: VirtualFileType.MULTI_PAGE_PDF,
      content,
      pages,
      file_meta: {},
    },
  } as TaskResult
}
